/**
 * Advanced-filter input for the internal organizations table.
 *
 * Accepts exactly the rules the shared filter toolbar can build for the table's
 * fields, and normalizes the toolbar's in-progress values (an empty text box, a
 * cleared number, an unpicked date, an empty selection) to `null` so the query
 * skips that rule rather than matching on a blank.
 */

import { z } from "zod";

import { OrganizationEnvironment } from "../constants";
import { type FilterLogic, filterLogicSchema } from "./filter-schemas";

const textRuleSchema = z.object({
  field: z.enum(["name", "slug"]),
  operator: z.enum([
    "contains",
    "not_contains",
    "equals",
    "not_equals",
    "starts_with",
    "ends_with",
    "is_empty",
    "is_not_empty",
  ]),
  value: z.string().transform((value) => (value === "" ? null : value)),
});

const numericFieldSchema = z.enum(["members", "projects", "totalSpent"]);

// A number input reports its raw text, so a typed value arrives as a string.
const numberValueSchema = z
  .union([z.number(), z.string()])
  .transform((value, ctx) => {
    if (typeof value === "string" && value.trim() === "") {
      return null;
    }
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Expected a number",
      });
      return z.NEVER;
    }
    return parsed;
  });

const numericRuleSchema = z.union([
  z.object({
    field: numericFieldSchema,
    operator: z.literal("between"),
    value: z.union([
      z.tuple([z.number().finite(), z.number().finite()]),
      // A fresh "between" rule starts with both bounds blank.
      z.tuple([z.literal(""), z.literal("")]).transform(() => null),
    ]),
  }),
  z.object({
    field: numericFieldSchema,
    operator: z.enum([
      "equals",
      "not_equals",
      "gt",
      "gte",
      "lt",
      "lte",
      "is_empty",
      "is_not_empty",
    ]),
    value: numberValueSchema,
  }),
]);

const dateRuleSchema = z.object({
  field: z.literal("createdAt"),
  operator: z.enum([
    "is_on",
    "is_before",
    "is_after",
    "is_within_last",
    "is_within_next",
    "is_empty",
    "is_not_empty",
  ]),
  // The date picker sends the picked day's local midnight as an ISO instant,
  // and null once the day is cleared.
  value: z
    .union([z.string().datetime(), z.literal(""), z.null()])
    .transform((value) => (value ? new Date(value) : null)),
});

const environmentRuleSchema = z.object({
  field: z.literal("environment"),
  operator: z.enum(["in", "not_in", "is_empty", "is_not_empty"]),
  value: z
    .union([z.array(z.nativeEnum(OrganizationEnvironment)), z.literal("")])
    .transform((value) =>
      Array.isArray(value) && value.length > 0 ? value : null,
    ),
});

const ruleSchema = z.union([
  textRuleSchema,
  numericRuleSchema,
  dateRuleSchema,
  environmentRuleSchema,
]);

function groupSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({ logic: filterLogicSchema, rules: z.array(item) });
}

// The toolbar nests groups at most three deep, counting the root.
const innermostGroupSchema = groupSchema(ruleSchema);
const nestedGroupSchema = groupSchema(
  z.union([ruleSchema, innermostGroupSchema]),
);
export const internalOrganizationFilterGroupSchema = groupSchema(
  z.union([ruleSchema, nestedGroupSchema]),
);

export type InternalOrganizationFilterRule = z.output<typeof ruleSchema>;

export interface InternalOrganizationFilterGroup {
  logic: FilterLogic;
  rules: (InternalOrganizationFilterRule | InternalOrganizationFilterGroup)[];
}
